// Nutritional values for Santa Chiara gelato.
// Final per-100g values = weighted average of the BASE + every selected ingredient.
// Base values are real (from the owner's Excel). Ingredient values are standard
// per-100g reference values (energy kcal, fat, saturated fat, carbs, sugars, protein, salt).

export const NUTRITION_BASES = {
  milk: { label: "Milk base", kcal: 136, fat: 4.3, sat: 2.4, carb: 20.8, sugar: 14.9, protein: 5.0, salt: 0.88 },
  plant: { label: "Sorbet / Vegan base", kcal: 117, fat: 0, sat: 0, carb: 30.0, sugar: 24.6, protein: 0, salt: 0.06 },
};

// Relative "parts" each component contributes to the final 100g (the base dominates).
const BASE_PART = 70;
const PARTS = { fruits: 12, creams: 14, crazy: 8, crunchy: 7, swirls: 7, spicy: 1 };

const CATEGORY_DEFAULT = {
  fruits: { kcal: 52, fat: 0.3, sat: 0.0, carb: 13, sugar: 10, protein: 0.6, salt: 0.0 },
  creams: { kcal: 260, fat: 16, sat: 9, carb: 24, sugar: 21, protein: 4.5, salt: 0.15 },
  crazy: { kcal: 70, fat: 3, sat: 1, carb: 7, sugar: 3, protein: 3, salt: 0.3 },
  crunchy: { kcal: 480, fat: 24, sat: 6, carb: 58, sugar: 28, protein: 8, salt: 0.4 },
  swirls: { kcal: 300, fat: 10, sat: 5, carb: 52, sugar: 46, protein: 2.5, salt: 0.1 },
  spicy: { kcal: 300, fat: 8, sat: 2, carb: 50, sugar: 3, protein: 10, salt: 0.1 },
};

// Notable per-ingredient overrides (per 100g) so values clearly vary by choice.
const OVERRIDES = {
  // Fruits
  Avocado: { kcal: 160, fat: 15, sat: 2, carb: 9, sugar: 0.7, protein: 2, salt: 0 },
  Banana: { kcal: 89, fat: 0.3, sat: 0.1, carb: 23, sugar: 12, protein: 1.1, salt: 0 },
  Coconut: { kcal: 354, fat: 33, sat: 30, carb: 15, sugar: 6, protein: 3.3, salt: 0 },
  Chestnut: { kcal: 213, fat: 2, sat: 0.4, carb: 46, sugar: 11, protein: 2.4, salt: 0 },
  Date: { kcal: 282, fat: 0.4, sat: 0, carb: 75, sugar: 63, protein: 2.5, salt: 0 },
  Fig: { kcal: 74, fat: 0.3, sat: 0, carb: 19, sugar: 16, protein: 0.8, salt: 0 },
  // Creams / nut & chocolate creams
  Pistachio: { kcal: 560, fat: 45, sat: 6, carb: 28, sugar: 8, protein: 20, salt: 0 },
  Hazelnut: { kcal: 628, fat: 61, sat: 4.5, carb: 17, sugar: 4.3, protein: 15, salt: 0 },
  Almond: { kcal: 579, fat: 50, sat: 3.8, carb: 22, sugar: 4.4, protein: 21, salt: 0 },
  "Peanut Butter": { kcal: 588, fat: 50, sat: 10, carb: 20, sugar: 9, protein: 25, salt: 0.4 },
  Gianduja: { kcal: 539, fat: 31, sat: 11, carb: 57, sugar: 56, protein: 6, salt: 0.1 },
  Nutella: { kcal: 539, fat: 31, sat: 11, carb: 57, sugar: 56, protein: 6, salt: 0.1 },
  "Nutella-style Hazelnut Cocoa": { kcal: 539, fat: 31, sat: 11, carb: 57, sugar: 56, protein: 6, salt: 0.1 },
  "Dark Chocolate 50%": { kcal: 500, fat: 30, sat: 18, carb: 55, sugar: 45, protein: 5, salt: 0.02 },
  "Dark Chocolate 65%": { kcal: 540, fat: 38, sat: 23, carb: 45, sugar: 35, protein: 6, salt: 0.02 },
  "Dark Chocolate 75%": { kcal: 560, fat: 43, sat: 26, carb: 38, sugar: 28, protein: 7, salt: 0.02 },
  "Dark Chocolate 80%": { kcal: 600, fat: 50, sat: 30, carb: 30, sugar: 20, protein: 8, salt: 0.02 },
  "Milk Chocolate": { kcal: 535, fat: 30, sat: 19, carb: 59, sugar: 52, protein: 7.6, salt: 0.08 },
  "White Chocolate": { kcal: 539, fat: 32, sat: 19, carb: 59, sugar: 59, protein: 5.9, salt: 0.15 },
  Vanilla: { kcal: 200, fat: 11, sat: 7, carb: 23, sugar: 22, protein: 3.5, salt: 0.1 },
  "Fior di Latte": { kcal: 200, fat: 11, sat: 7, carb: 23, sugar: 22, protein: 3.5, salt: 0.1 },
  Milk: { kcal: 200, fat: 11, sat: 7, carb: 23, sugar: 22, protein: 3.5, salt: 0.1 },
  Yogurt: { kcal: 120, fat: 4, sat: 2.5, carb: 16, sugar: 15, protein: 4, salt: 0.1 },
  // Crunchy
  Hazelnuts: { kcal: 628, fat: 61, sat: 4.5, carb: 17, sugar: 4.3, protein: 15, salt: 0 },
  Almonds: { kcal: 579, fat: 50, sat: 3.8, carb: 22, sugar: 4.4, protein: 21, salt: 0 },
  Pistachios: { kcal: 560, fat: 45, sat: 6, carb: 28, sugar: 8, protein: 20, salt: 0 },
  Peanuts: { kcal: 567, fat: 49, sat: 7, carb: 16, sugar: 4, protein: 26, salt: 0 },
  Oreo: { kcal: 480, fat: 20, sat: 6, carb: 71, sugar: 38, protein: 5, salt: 0.7 },
  "Lotus Biscoff": { kcal: 502, fat: 24, sat: 10, carb: 64, sugar: 38, protein: 4, salt: 0.8 },
  Pecan: { kcal: 691, fat: 72, sat: 6, carb: 14, sugar: 4, protein: 9, salt: 0 },
  "Brazil Nuts": { kcal: 659, fat: 67, sat: 16, carb: 12, sugar: 2, protein: 14, salt: 0 },
  "Caramelized Peanuts": { kcal: 520, fat: 28, sat: 5, carb: 55, sugar: 38, protein: 16, salt: 0.1 },
  "Caramelized Almonds": { kcal: 530, fat: 33, sat: 3, carb: 48, sugar: 35, protein: 13, salt: 0.1 },
  Raisins: { kcal: 299, fat: 0.5, sat: 0.1, carb: 79, sugar: 59, protein: 3, salt: 0.05 },
  "Lemon zest": { kcal: 47, fat: 0.3, sat: 0, carb: 16, sugar: 4, protein: 1.5, salt: 0 },
  "Lime zest": { kcal: 47, fat: 0.3, sat: 0, carb: 16, sugar: 4, protein: 1.5, salt: 0 },
  "Orange zest": { kcal: 47, fat: 0.2, sat: 0, carb: 16, sugar: 9, protein: 1.5, salt: 0 },
  "Amarena cherry": { kcal: 250, fat: 0.1, sat: 0, carb: 62, sugar: 58, protein: 0.5, salt: 0 },
  "Peanut butter": { kcal: 588, fat: 50, sat: 10, carb: 20, sugar: 9, protein: 25, salt: 0.4 },
};

function profileFor(category, label) {
  return OVERRIDES[label] || CATEGORY_DEFAULT[category];
}

export function computeNutrition(state = {}) {
  const styles = state.styles || [];
  const isPlant = styles.includes("sorbet") || styles.includes("vegan");
  const base = isPlant ? NUTRITION_BASES.plant : NUTRITION_BASES.milk;

  const keys = ["kcal", "fat", "sat", "carb", "sugar", "protein", "salt"];
  const acc = {};
  keys.forEach((k) => (acc[k] = base[k] * BASE_PART));
  let totalParts = BASE_PART;

  for (const cat of Object.keys(PARTS)) {
    const items = state[cat] || [];
    for (const label of items) {
      const p = profileFor(cat, label);
      const part = PARTS[cat];
      keys.forEach((k) => (acc[k] += (p[k] || 0) * part));
      totalParts += part;
    }
  }

  const r1 = (v) => Math.round((v / totalParts) * 10) / 10;
  const kcal = Math.round(acc.kcal / totalParts);
  return {
    label: base.label,
    baseType: isPlant ? "plant" : "milk",
    energy_kcal: kcal,
    energy_kj: Math.round(kcal * 4.184),
    fat_g: r1(acc.fat),
    saturated_g: r1(acc.sat),
    carbs_g: r1(acc.carb),
    sugars_g: r1(acc.sugar),
    protein_g: r1(acc.protein),
    salt_g: Math.round((acc.salt / totalParts) * 100) / 100,
  };
}

// ---- Allergen detection from the customer's selections ----
const CREAM_EXTRA = {
  "Custard Cream": ["Eggs"], "Crème Brûlée": ["Eggs"], "Zabaglione": ["Eggs"],
  Tiramisu: ["Eggs", "Gluten"], "Cannoli Cream": ["Eggs", "Gluten"], Cheesecake: ["Gluten"],
  "Cookies & Cream": ["Gluten", "Soy"], Panettone: ["Gluten", "Eggs"],
  "Kinder Bueno": ["Gluten", "Soy"], "Kinder Chocolate": ["Soy"], "Kinder Country": ["Gluten", "Soy"],
  Almond: ["Tree nuts"], Hazelnut: ["Tree nuts"], Pistachio: ["Tree nuts"],
  Gianduja: ["Tree nuts", "Soy"], Nutella: ["Tree nuts", "Soy"],
  "Nutella-style Hazelnut Cocoa": ["Tree nuts", "Soy"], "Peanut Butter": ["Peanuts"],
  "White Chocolate": ["Soy"], "Milk Chocolate": ["Soy"],
};
const CRUNCHY_MAP = {
  Oreo: ["Gluten", "Soy"], "Lotus Biscoff": ["Gluten"], Ladyfingers: ["Gluten", "Eggs"],
  Hazelnuts: ["Tree nuts"], Pistachios: ["Tree nuts"], Peanuts: ["Peanuts"],
  Almonds: ["Tree nuts"], "Chocolate Crumble": ["Gluten", "Soy", "Milk"], "Cookie Crumble": ["Gluten", "Soy"],
  "Caramelized Peanuts": ["Peanuts"], "Caramelized Almonds": ["Tree nuts"], Pecan: ["Tree nuts"], "Brazil Nuts": ["Tree nuts"],
};
const SWIRL_MAP = {
  Hazelnut: ["Tree nuts"], Almond: ["Tree nuts"], Pistachio: ["Tree nuts"],
  Chocolate: ["Soy", "Milk"], "White Chocolate": ["Soy", "Milk"], Caramel: ["Milk"],
  "Peanut butter": ["Peanuts"],
};
const CRAZY_MAP = {
  Camembert: ["Milk"], Gorgonzola: ["Milk"], "Parmigiano Reggiano": ["Milk"],
  "Smoked Salmon": ["Fish"], Celery: ["Celery"], Sesame: ["Sesame"],
};
const ALLERGEN_ORDER = ["Milk", "Eggs", "Tree nuts", "Peanuts", "Soy", "Gluten", "Sesame", "Fish", "Celery", "Sulphites"];

export function detectAllergens(state) {
  const found = new Set();
  const styles = state.styles || [];
  const isPlant = styles.includes("sorbet") || styles.includes("vegan");
  if (!isPlant) found.add("Milk");
  if (styles.includes("dairy") || styles.includes("lactose-free")) found.add("Milk");
  if ((state.creams || []).length > 0) found.add("Milk");
  for (const c of state.creams || []) (CREAM_EXTRA[c] || []).forEach((a) => found.add(a));
  for (const c of state.crunchy || []) (CRUNCHY_MAP[c] || []).forEach((a) => found.add(a));
  for (const s of state.swirls || []) (SWIRL_MAP[s] || []).forEach((a) => found.add(a));
  for (const c of state.crazy || []) (CRAZY_MAP[c] || []).forEach((a) => found.add(a));
  let alcoholNote = "";
  if (state.alcohol_type) {
    alcoholNote = `Contains alcohol (${state.alcohol_type})`;
    if (state.alcohol_type === "Prosecco") found.add("Sulphites");
  }
  const allergens = ALLERGEN_ORDER.filter((a) => found.has(a));
  return { allergens, alcoholNote };
}
